import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, Download, FileSpreadsheet, Layers, ShieldCheck, Table, Zap } from 'lucide-react'
import { documentsApi, type DocumentPreview, type ExportResponse } from '../api'
import ChandraScanButton from '../components/ChandraScanButton'
import SplitScreenPreview from '../components/SplitScreenPreview'
import SubscriptionNotice from '../components/SubscriptionNotice'
import UpgradeModal from '../components/UpgradeModal'
import { useToastStore } from '../store/toast'

export default function Preview() {
  const { documentId } = useParams<{ documentId: string }>()
  const addToast = useToastStore((state) => state.addToast)
  const [preview, setPreview] = useState<DocumentPreview | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx')
  const [exportResult, setExportResult] = useState<ExportResponse['export'] | null>(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  const loadPreview = useCallback(() => {
    if (!documentId) return
    setLoading(true)
    documentsApi.preview(documentId)
      .then((res) => {
        setPreview(res.data)
        setSelectedIds(res.data.columns.map((column) => column.id))
      })
      .catch((err) => addToast({ type: 'error', message: err.response?.data?.error || 'Could not load preview.' }))
      .finally(() => setLoading(false))
  }, [documentId, addToast])

  useEffect(() => {
    loadPreview()
  }, [loadPreview])

  const selectedCount = selectedIds.length
  const sampleTable = preview?.sample_table || preview?.tables[0]
  const toggle = (id: string) => {
    setSelectedIds((ids) => ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id])
  }

  const exportFile = async () => {
    if (!documentId || selectedIds.length === 0) return
    setExporting(true)
    try {
      const response = await documentsApi.export(documentId, selectedIds, format)
      setExportResult(response.data.export)
      addToast({ type: 'success', message: `Exported ${response.data.export.columns_exported} columns.` })
    } catch (err: any) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Export failed.' })
      if (err.response?.data?.upgrade_required) setUpgradeOpen(true)
    } finally {
      setExporting(false)
    }
  }

  if (loading) return <div className="max-w-7xl mx-auto text-gray-500">Loading preview...</div>
  if (!preview) return null

  return (
    <div className="max-w-7xl mx-auto space-y-6 min-w-0 overflow-x-hidden">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-950">Data Preview</h1>
          <p className="text-gray-600 mt-1">{preview.document.original_filename}</p>
        </div>
        <Link to="/dashboard" className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Back to dashboard</Link>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Info label="Pages" value={preview.page_count} />
        <Info label="Rows" value={preview.row_count} />
        <Info label="Columns" value={preview.column_count} />
        <Info label="Selected" value={`${selectedCount}/${preview.columns.length}`} />
      </div>

      <SubscriptionNotice context="preview" />

      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden text-gray-950 max-w-full">
        <div className="p-5 border-b border-gray-200 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Layers className="w-5 h-5 text-brand-blue" />
            <div>
              <h2 className="font-semibold text-gray-950">PDF and data review</h2>
              <p className="text-sm text-gray-600">Review the source PDF beside the extracted table before choosing export columns.</p>
            </div>
          </div>
          <span className="text-sm text-gray-500">{preview.tables.length} detected table{preview.tables.length === 1 ? '' : 's'}</span>
        </div>
        <div className="h-[680px] min-h-[560px]">
          <SplitScreenPreview
            documentId={documentId!}
            pdfUrl={documentsApi.fileUrl(documentId!)}
            tables={preview.tables}
            columns={preview.columns}
            selectedIds={selectedIds}
            toggle={toggle}
            showFullPreviewButton={false}
          />
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-brand-blue mt-0.5" />
            <div>
              <h2 className="font-semibold text-gray-950">Detected extraction profile</h2>
              <p className="text-sm text-gray-600">
                {profileLabel(preview.profile)} with {Math.round((preview.confidence || 0) * 100)}% confidence.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            {preview.quality_report.missing_rows !== undefined && <Metric label="Missing rows" value={preview.quality_report.missing_rows} />}
            {preview.quality_report.sum_previous !== undefined && <Metric label="Previous total" value={formatNumber(preview.quality_report.sum_previous)} />}
            {preview.quality_report.sum_annual !== undefined && <Metric label="Annual total" value={formatNumber(preview.quality_report.sum_annual)} />}
          </div>
        </div>
        {(preview.quality_report.warnings || []).length > 0 && (
          <div className="mt-4 space-y-2">
            {(preview.quality_report.warnings || []).map((warning) => (
              <div key={warning} className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <AlertTriangle className="w-4 h-4" />
                {warning}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white border border-purple-200 rounded-lg p-5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <h2 className="font-semibold text-gray-950">Advanced OCR test</h2>
              <p className="text-sm text-gray-600">
                Run Advanced OCR on page 1, refresh this preview with enhanced tables, then inspect the PDF beside the data.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ChandraScanButton documentId={documentId!} pageRange="1" onScanComplete={loadPreview} />
            <Link
              to={`/documents/${documentId}/advanced-preview`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50"
            >
              <Layers className="w-4 h-4" />
              Split-screen view
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden text-gray-950 max-w-full">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Table className="w-5 h-5 text-brand-blue" />
            <div>
              <h2 className="font-semibold text-gray-950">Select columns from headers</h2>
              <p className="text-sm text-gray-600">{selectedCount} columns selected for export.</p>
            </div>
          </div>
          <button
            onClick={() => setSelectedIds(selectedCount === preview.columns.length ? [] : preview.columns.map((column) => column.id))}
            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            {selectedCount === preview.columns.length ? 'Unselect all' : 'Select all'}
          </button>
        </div>
        <div className="overflow-x-auto max-w-full">
          <div className="min-w-max p-4">
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${preview.columns.length}, minmax(9rem, 1fr))` }}>
              {preview.columns.map((column) => (
                <button
                  key={column.id}
                  onClick={() => toggle(column.id)}
                  className={`rounded border p-3 text-right arabic-text ${
                    selectedIds.includes(column.id) ? 'border-brand-blue bg-brand-light text-brand-blue' : 'border-gray-200 bg-white text-gray-600'
                  }`}
                >
                  <input type="checkbox" checked={selectedIds.includes(column.id)} readOnly className="ml-2" />
                  {column.column_name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden text-gray-950 max-w-full">
        <div className="p-5 border-b border-gray-200">
          <h2 className="font-semibold text-gray-950">Sample rows</h2>
          <p className="text-sm text-gray-600">
            Showing page {sampleTable?.page}, table {sampleTable?.table_index} with {sampleTable?.row_count || 0} rows and {sampleTable?.column_count || 0} columns.
          </p>
        </div>
        <div className="overflow-x-auto max-w-full">
          <table className="text-sm min-w-max">
            <thead className="bg-gray-50">
              <tr>
                {(sampleTable?.columns || []).map((column, index) => (
                  <th key={`${column}-${index}`} className="px-3 py-3 text-right text-xs font-semibold text-gray-700 arabic-text whitespace-nowrap">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(sampleTable?.rows || []).slice(0, 15).map((row, rowIndex) => (
                <tr key={rowIndex} className="border-t border-gray-100">
                  {(sampleTable?.columns || []).map((column, columnIndex) => (
                    <td key={`${column}-${columnIndex}`} className="px-3 py-2 text-right text-gray-950 arabic-text whitespace-nowrap">{row[columnIndex] || ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <FileSpreadsheet className="w-5 h-5 text-brand-blue" />
          <div>
            <h2 className="font-semibold text-gray-950">Export</h2>
            <p className="text-sm text-gray-600">Exports include all rows for selected columns.</p>
          </div>
        </div>
        <div className="md:hidden">
          <SubscriptionNotice context="export" />
        </div>
        <div className="flex items-center gap-3">
          <select value={format} onChange={(e) => setFormat(e.target.value as 'xlsx' | 'csv')} className="px-3 py-2 border border-gray-300 rounded-lg">
            <option value="xlsx">Excel (.xlsx)</option>
            <option value="csv">CSV (.csv)</option>
          </select>
          <button
            onClick={exportFile}
            disabled={exporting || selectedIds.length === 0}
            className="px-4 py-2 rounded-lg bg-brand-blue text-white hover:bg-brand-accent disabled:opacity-50"
          >
            {exporting ? 'Exporting...' : 'Export selected'}
          </button>
          {exportResult && (
            <div className="flex flex-col gap-2">
              <a href={exportResult.download_url} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                <Download className="w-4 h-4" />
                Download
              </a>
              <p className="max-w-xs text-xs text-amber-700">
                Free accounts include 1 export per document. Upgrade for unlimited downloads and full-document processing.
              </p>
            </div>
          )}
        </div>
      </section>

      <SubscriptionNotice context="export" className="hidden md:block" />
      {exportResult && <SubscriptionNotice context="download" />}
      <UpgradeModal isOpen={upgradeOpen} onClose={() => setUpgradeOpen(false)} currentPlan="free" />
    </div>
  )
}

function Info({ label, value }: { label: string; value: any }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-950 mt-2">{value}</p>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: any }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
      <p className="text-gray-500">{label}</p>
      <p className="font-semibold text-gray-950 mt-1">{value}</p>
    </div>
  )
}

function profileLabel(profile: string) {
  if (profile.includes('wide')) return 'Wide recurring table'
  if (profile.includes('financial')) return 'Structured financial table'
  return 'Generic document table'
}

function formatNumber(value: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)
}
